import { Component, EventEmitter, OnInit } from '@angular/core';
import { BaseComponent } from 'src/app/core/base.component';
import { SessionService } from 'src/app/shared/services/session.service';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';
import { LeagueMemberService } from '../services/league-member.service';
import { LeagueService } from '../services/league.service';
import { LeagueMember, LeagueMemberList } from '../models/league-member';
import { League } from '../models/league';
import { Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { LeagueMembersListQueryFilter } from './league-members-list-query-filter';
import { provideNativeDateAdapter } from '@angular/material/core';
import { AlertModalComponent } from 'src/app/shared/modals/alert-modal/alert-modal.component';
import { CreateLeagueMemberModalComponent } from '../modals/league-member/create-league-member-modal/create-league-member-modal.component';
import { EditLeagueMemberModalComponent } from '../modals/league-member/edit-league-member-modal/edit-league-member-modal.component';
import { AddMembersToIterationModalComponent } from '../modals/league-member/add-members-to-iteration-modal/add-members-to-iteration-modal.component';

@Component({
    selector: 'league-members-list',
    templateUrl: './league-members-list.component.html',
    styleUrl: './league-members-list.component.scss',
    providers: [provideNativeDateAdapter()],
    standalone: false
})
export class LeagueMembersListComponent extends BaseComponent implements OnInit {
    public members: LeagueMemberList[] = [];
    public filter: LeagueMembersListQueryFilter = new LeagueMembersListQueryFilter();
    public updateFilterEvent = new EventEmitter<any>();
    public displayedColumns: string[] = ['firstName', 'lastName', 'email', 'actions'];
    public selectedLeagueId: string | null = null;
    public leagues: League[] = [];

    constructor(
        sessionService: SessionService,
        stringHelpers: StringHelpers,
        private dialog: MatDialog,
        private leagueMemberService: LeagueMemberService,
        private leagueService: LeagueService
    ) {
        super(sessionService, stringHelpers);
        this.isDarkMode = !this.sessionService.getUser().defaultTheme;
    }

    ngOnInit(): void {
        this.filter.organizationId = this.sessionService.getUser().organizationId;
        this.loadLeagues();
    }

    loadLeagues(): void {
        this.leagueService.getAllByOrganization({
            organizationId: this.sessionService.getUser().organizationId
        }).subscribe({
            next: (result: any) => {
                this.leagues = result.results || [];
                if (this.leagues.length > 0 && !this.selectedLeagueId) {
                    this.selectedLeagueId = this.leagues[0].id;
                    this.getMembers();
                }
            },
            error: (error: any) => {
                console.error(error);
            }
        });
    }

    getMembers(): void {
        if (!this.selectedLeagueId) {
            return;
        }

        this.isLoading = true;
        this.filter.leagueId = this.selectedLeagueId;
        this.leagueMemberService.getAllByLeagueId(this.filter).subscribe({
            next: (result: any) => {
                this.members = result.results || [];
                this.filter.totalCount = result.totalCount;
                this.updateFilterEvent.emit(result.results);
                this.isLoading = false;
            },
            error: (error: any) => {
                this.isLoading = false;
                console.error(error);
                this.alert(error);
            }
        });
    }

    onLeagueChange(): void {
        this.filter.page = 1;
        this.getMembers();
    }

    sortData(sort: Sort): void {
        const data = this.members.slice();
        if (!sort.active || sort.direction === '') {
            this.members = data;
            return;
        }

        this.members = data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';
            switch (sort.active) {
                case 'firstName':
                    return this.compare(a.firstName, b.firstName, isAsc);
                case 'lastName':
                    return this.compare(a.lastName, b.lastName, isAsc);
                case 'email':
                    return this.compare(a.email, b.email, isAsc);
                default:
                    return 0;
            }
        });
    }

    compare(a: number | string, b: number | string, isAsc: boolean): number {
        return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    resetFilter(): void {
        this.filter = new LeagueMembersListQueryFilter();
        this.filter.organizationId = this.sessionService.getUser().organizationId;
        this.filter.leagueId = this.selectedLeagueId;
        this.filter.pageSize = 10;
    }

    onCreate(): void {
        if (!this.selectedLeagueId) {
            this.alert({
                status: 400,
                error: [{ errorMessage: 'Please select a league first' }]
            });
            return;
        }

        const selectedLeague = this.leagues.find(l => l.id === this.selectedLeagueId);
        const dialogRef = this.dialog.open(CreateLeagueMemberModalComponent, {
            maxWidth: '90vw',
            data: selectedLeague
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'save') {
                this.getMembers();
            }
        });
    }

    onEdit(member: LeagueMember): void {
        const dialogRef = this.dialog.open(EditLeagueMemberModalComponent, {
            maxWidth: '90vw'
        });

        dialogRef.componentInstance.member = member;

        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'save') {
                this.getMembers();
            }
        });
    }

    onDelete(member: LeagueMember): void {
        const dialogRef = this.dialog.open(AlertModalComponent, {
            width: '35vw',
        });

        dialogRef.componentInstance.title = 'Delete League Member';
        dialogRef.componentInstance.body = `<h3 class="text-danger mat-h3">WARNING! THIS ACTION CANNOT BE UNDONE</h3> <br /> 
        Are you sure you want to delete member <strong>${member.firstName} ${member.lastName}</strong>? 
        <br /><br />This will remove them from all league iterations.`;

        dialogRef.componentInstance.submitText = 'Yes';
        dialogRef.componentInstance.otherText = 'No';
        dialogRef.componentInstance.isOtherTextDisabled = false;

        dialogRef.afterClosed().subscribe((response) => {
            if (response === 'Yes') {
                this.isLoading = true;
                this.leagueMemberService.delete({
                    id: member.id,
                    lastEditedBy: this.sessionService.getUserId(),
                    organizationId: this.sessionService.getUser().organizationId,
                }).subscribe({
                    next: (result) => {
                        this.getMembers();
                        this.isLoading = false;
                    },
                    error: (error) => {
                        this.alert(error);
                        console.error(error);
                        this.isLoading = false;
                    }
                });
            }
        });
    }

    onAddToIteration(member: LeagueMember): void {
        const selectedLeague = this.leagues.find(l => l.id === this.selectedLeagueId);
        const dialogRef = this.dialog.open(AddMembersToIterationModalComponent, {
            maxWidth: '90vw',
            data: {
                league: selectedLeague,
                members: [member]
            }
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'save') {
                this.getMembers();
            }
        });
    }

    alert(error: any): void {
        const dialogRef = this.dialog.open(AlertModalComponent, {
            width: '35vw',
        });

        let errorMessage = '<h3 class="mat-h3">You have following errors:</h3><ul> ';

        if (error.status === 400) {
            if (Array.isArray(error.error)) {
                error.error.forEach((error: any) => {
                    errorMessage += `<li>${error.errorMessage}</li>`;
                });
            } else {
                errorMessage += `<li>${error.error}</li>`;
            }
        } else {
            errorMessage += `<li>There was an error loading the league members, Please try again or contact support if the problem persists</li>`;
        }

        dialogRef.componentInstance.title = 'League Members Error';
        dialogRef.componentInstance.body = errorMessage + '</ul>';
        dialogRef.componentInstance.submitText = 'OK';
    }
}

